import { useEffect, useRef, useState, type CSSProperties, type ReactNode, type Ref } from "react";

type RevealTag = "div" | "span" | "p" | "h2";

interface RevealProps {
  children: ReactNode;
  /** Element to render. Matches the tag the design already used. */
  as?: RevealTag;
  /** Starting vertical offset in px; animates to 0. */
  y?: number;
  /** Starting horizontal offset in px; animates to 0. */
  x?: number;
  /** Seconds to wait before starting. Used to stagger grids. */
  delay?: number;
  /** Seconds the fade/slide takes. */
  duration?: number;
  /** Play on mount instead of waiting for the element to scroll into view. */
  immediate?: boolean;
  className?: string;
  style?: CSSProperties;
}

/**
 * Fade-and-slide entrance animation, driven by CSS transitions.
 *
 * This replaces the equivalent framer-motion `initial`/`animate`/`whileInView`
 * usage across the site. The library was ~125KB of JavaScript that the entry
 * chunk had to download and parse before React could mount — for animations
 * that are two transitioned properties. `opacity` and `transform` are both
 * compositor-friendly, so the result is the same visually but costs nothing on
 * the main thread.
 */
const Reveal = ({
  children,
  as: Tag = "div",
  y = 0,
  x = 0,
  delay = 0,
  duration = 0.5,
  immediate = false,
  className,
  style,
}: RevealProps) => {
  const ref = useRef<HTMLElement>(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    if (shown) return;

    // Honour a reduced-motion preference by skipping straight to the end state.
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      setShown(true);
      return;
    }

    if (immediate) {
      // Next frame, so the browser has painted the initial state and actually
      // runs the transition rather than jumping to the end.
      const id = requestAnimationFrame(() => setShown(true));
      return () => cancelAnimationFrame(id);
    }

    const el = ref.current;
    if (!el) return;
    if (!("IntersectionObserver" in window)) {
      setShown(true);
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        setShown(true);
        observer.disconnect();
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [shown, immediate]);

  const transform = shown ? "none" : `translate3d(${x}px, ${y}px, 0)`;

  return (
    <Tag
      ref={ref as Ref<never>}
      className={className}
      style={{
        ...style,
        opacity: shown ? 1 : 0,
        transform,
        transition: `opacity ${duration}s ease-out, transform ${duration}s ease-out`,
        transitionDelay: delay ? `${delay}s` : undefined,
      }}
    >
      {children}
    </Tag>
  );
};

export default Reveal;
